import { useAuth0 } from '@auth0/auth0-react';
import React, { useState, useRef, useContext, useEffect } from 'react';
import { store } from 'components/Store';
import getUrlParam, { getUrlParamRaw, decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import useWindowHeight from 'components/Dashboard/utilities/useWindowHeight';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import { PortalWithState } from 'react-portal';
import ConfigData from 'config/ConfigData';
import ResearchPopUp from 'components/Dashboard/SubNav/ui/ResearchPopUp';
import useResetReference from 'components/Dashboard/SubNav/useResetReference';
import LocalStorage from 'utils/LocalStorage';
import GimmickPopUp from 'components/Dashboard/components/GimmickPopup';
import dayjs from 'dayjs';
import applyDoubleStraightQuotes from 'utils/applyDoubleStraightQuotes';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';

const useInputSearchResultsFunction = (props) => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { searchResultsReturnUrl, sectionsView, search } = state;
  const { setContentResults } = useContentResults();
  const { openInNewTab, closeFunction, setOpenSearchBar = () => {}, setRecentSearches } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const { inboxSection } = useMainSectionParameter();
  const { resetReference } = useResetReference();

  const resetSearchResults = () => {
    setContentResults({});
  };

  const searchResults = (value) => {
    resetSearchResults();
    let recentSearches = !!LocalStorage.getItem('recentSearches')
      ? JSON.parse(LocalStorage.getItem('recentSearches'))
      : [];
    recentSearches = [...recentSearches, { value, date: dayjs().format('YYYY-MM-DD HH:mm:ss') }];
    LocalStorage.setItem('recentSearches', JSON.stringify(recentSearches));
    if (setRecentSearches) {
      setRecentSearches(recentSearches);
    }
    if (openInNewTab) {
      window.open(`/search-results?search=${encodeURIComponent(value)}`, '_blank');
    } else {
      navigate(`/search-results?search=${encodeURIComponent(value)}`);
    }
    dispatch({ type: 'MODIFY_SECTION', parameter: 'totalNumbers', value: {} });
    LocalStorage.removeItem('contentFilters');
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'searchResultsReturnUrl',
      value: [...searchResultsReturnUrl, encodeURIComponent(`${location.pathname}${location.search}${location.hash}`)],
    });
    setOpenSearchBar(false);
    if (closeFunction) {
      closeFunction();
    }
  };

  const clearFunction = () => {
    if (!inboxSection && !location.pathname.includes('/stakeholder-mapping')) {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'sectionsView',
        value: {
          ...sectionsView,
          selectedOptionParliamentLiveView: 'Relevant events',
        },
      });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'totalNumbers', value: {} });
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'search',
        value: {
          ...search,
          activeSearch: false,
          query: '',
          list: null,
        },
      });
      resetReference();
      if (location.pathname.includes('stakeholders') || location.pathname.includes('upcoming-events')) {
        navigate({ pathname: `/search-results/${ConfigData?.monitorInboxInitialPage ?? 'key-updates'}` });
      } else {
        navigate({ pathname: location.pathname, search: '' });
      }
    }
  };

  return { searchResults, clearFunction };
};

const InputComponent = React.forwardRef((props, ref) => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { search, clearValueSimpleSearchBar } = state;
  const {
    homePage,
    setOpenSearchBar = () => {},
    openSearchBar,
    closeFunction,
    enforceShortPlaceholder,
    openInNewTab,
    placeholder,
    onBlur = () => {},
    preventInitialFocus,
    inactiveState,
    setRecentSearches,
  } = props;

  const { isAuthenticated } = useAuth0();
  const { gimmicks } = search;
  const searchValueFromUrl = getUrlParamRaw('search');
  const location = useLocation();
  const params = useParams();
  const { windowWidth } = useWindowHeight();
  const [valueSearch, setValueSearch] = useState(
    !!searchValueFromUrl && location.pathname.includes('/search-results') ? decodeUrlQueryValue(searchValueFromUrl) : ''
  );
  const { searchResultsSection } = useMainSectionParameter(params);
  const inputSearch = ref || React.createRef();
  const { searchResults, clearFunction } = useInputSearchResultsFunction({
    openInNewTab,
    closeFunction,
    setOpenSearchBar,
    setRecentSearches,
  });

  const changeValue = (value) => {
    setValueSearch(value);
  };

  const showGimmick = (word) => {
    dispatch({ type: 'MODIFY_SECTION', parameter: 'gimmickWord', value: word });
    dispatch({ type: 'MODIFY_SECTION', parameter: 'showGimmickPopup', value: true });
  };

  const focusInput = useRef(null);
  focusInput.current = () => {
    if ((homePage || openSearchBar) && !preventInitialFocus) {
      setTimeout(() => {
        if (inputSearch.current) {
          inputSearch.current.focus();
        }
      }, 300);
    }
  };

  const clearSearchSimpleBar = () => {
    //AE: In a future implementation could be worth review if just updating the url we can update the state of the search
    clearValue();
    clearFunction();
  };

  const clearValue = () => {
    setValueSearch('');
    inputSearch.current.focus();
  };

  useEffect(() => {
    focusInput.current();
  }, [openSearchBar]);

  useEffect(() => {
    //AE: It was not possible to compare the reference state with a local reference to hide the searchbar
    if (clearValueSimpleSearchBar) {
      setOpenSearchBar(false);
      setValueSearch('');
      focusInput.current();
      dispatch({ type: 'MODIFY_SECTION', parameter: 'clearValueSimpleSearchBar', value: false });
    }
  }, [clearValueSimpleSearchBar]);

  const updateSimpleValue = useRef(null);
  updateSimpleValue.current = () => {
    if (searchResultsSection) {
      if (searchValueFromUrl && decodeUrlQueryValue(searchValueFromUrl) !== valueSearch) {
        setValueSearch(decodeUrlQueryValue(searchValueFromUrl));
      }
      if (getUrlParam('or') && valueSearch !== '') {
        setValueSearch('');
      }
    } else {
      if (!valueSearch !== '') {
        setValueSearch('');
      }
    }
  };

  useEffect(() => {
    updateSimpleValue.current();
  }, [location.search, searchResultsSection]);

  return (
    <>
      <div className='flex-grow-1'>
        <input
          className={`search-main-input w-100`}
          data-hj-allow
          type='text'
          value={valueSearch}
          onChange={(e) => {
            let value = e.target.value;
            changeValue(value);
          }}
          onKeyDown={(e) => {
            let value = e.target.value?.trim();
            if (e.keyCode === 13 && value !== '') {
              inputSearch.current.blur();
              if (gimmicks.includes(value.toLowerCase().trim()) && !isAuthenticated) {
                showGimmick(value);
              } else {
                value = applyDoubleStraightQuotes(value);
                searchResults(value);
              }
            } else if (e.keyCode === 9) {
              e.preventDefault();
            }
          }}
          ref={inputSearch}
          placeholder={
            placeholder
              ? placeholder
              : enforceShortPlaceholder
                ? 'Search PolicyMogul'
                : windowWidth > 991 && windowWidth < 1130
                  ? 'Search'
                  : 'Search the political landscape'
          }
          onBlur={onBlur}
        />
      </div>
      {/* CLOSE BUTTON */}
      {!inactiveState && (
        <div
          className='close-icon-container'
          style={{
            visibility: valueSearch !== '' && (openSearchBar || homePage) ? 'visible' : 'hidden',
          }}
        >
          <button
            className='general-button close-icon'
            data-tooltip-content='Clear search'
            data-tooltip-id='general-tooltip'
            data-tooltip-place='right'
            onClick={(e) => {
              e.stopPropagation();
              if (getUrlParam('search')) {
                clearSearchSimpleBar();
              } else {
                clearValue();
              }
            }}
          />
        </div>
      )}
      <GimmickPopUp />
    </>
  );
});

const SimpleSearchBar = (props) => {
  const { homePage } = props;
  const [openSearchBar, setOpenSearchBar] = useState(false);
  const params = useParams();
  const { searchResultsSection } = useMainSectionParameter(params);
  const location = useLocation();
  const homeLocation = location.pathname === '/';
  const inputSearch = useRef(null);

  return (
    <>
      <div className='position-relative'>
        <div
          className={`simple-searchbar mr-lg-3 flex-centered ${homePage ? 'simple-searchbar-open' : ''} ${
            searchResultsSection && !homePage ? 'simple-searchbar-active' : ''
          } ${homeLocation ? 'simple-searchbar-home' : ''}`}
          data-tooltip-content={homePage ? '' : 'Search'}
          data-tooltip-id={'general-tooltip'}
          onClick={() => {
            if (!homePage) {
              setOpenSearchBar(!openSearchBar);
            }
          }}
        >
          <span className={`main-search-icon`} />
          <InputComponent ref={inputSearch} {...props} setOpenSearchBar={setOpenSearchBar} />
        </div>
        {openSearchBar && (
          <>
            <div
              className='close-tips'
              onClick={() => {
                setOpenSearchBar(!openSearchBar);
              }}
            />
            <div className={`box filters-container search-filter-box top-nav-search-box`}>
              <p className='title'>Search</p>
              <div className='flex-centered box-rounded mb-2 top-search-input-container'>
                <InputComponent
                  ref={inputSearch}
                  {...props}
                  setOpenSearchBar={setOpenSearchBar}
                  openSearchBar={openSearchBar}
                />
              </div>
              <AdvancedSearch setOpenSearchBar={setOpenSearchBar} />
            </div>
          </>
        )}
      </div>
    </>
  );
};

const AdvancedSearch = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { referenceState, activePlan } = state;
  const { enableAdvancedSearch } = activePlan;
  const { setOpenSearchBar } = props;
  const { isAuthenticated } = useAuth0();
  return (
    <PortalWithState>
      {({ openPortal, closePortal, portal }) => (
        <>
          <button
            className='general-button main-disabled-text text-underline paragraph-p2 mb-3'
            onClick={() => {
              if (enableAdvancedSearch) {
                openPortal();
              }
            }}
            data-tooltip-content={
              !enableAdvancedSearch || !isAuthenticated
                ? 'Advanced search is available on any paid plan, and allows you to home in on relevant content and avoid information overload'
                : ''
            }
            data-tooltip-id={'general-tooltip'}
            data-tooltip-class-name={'header-tooltip pricing-tooltip-description'}
          >
            Advanced search{' '}
            {!enableAdvancedSearch && (
              <img
                src={`${import.meta.env.VITE_APP_CDNURL}/images/padlock.svg`}
                className='padlock-title-item'
                alt='padlock feature blocked'
              />
            )}
          </button>
          {portal(
            <ResearchPopUp
              setShowPopup={closePortal}
              showPopUp={true}
              referenceState={referenceState}
              setOpenSearchBar={setOpenSearchBar}
              keywordsSection={'keywords'}
            />
          )}
        </>
      )}
    </PortalWithState>
  );
};

export { InputComponent, useInputSearchResultsFunction };
export default SimpleSearchBar;
